import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { initPushNotifications } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=user
  const [error, setError] = useState("");

  const refreshMe = useCallback(async () => {
    const t = localStorage.getItem("kapei_token");
    if (!t) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      initPushNotifications(data);
    } catch (e) {
      // En móvil/WebView puede haber fallos puntuales al restaurar sesión
      // al volver a abrir la app. No borramos el token salvo que el servidor
      // confirme que ya no es válido.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("kapei_token");
        setUser(false);
      } else {
        // Mantener estado de carga/último estado; el siguiente refresh volverá a intentarlo.
        setUser((cur) => cur || false);
      }
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("kapei_token", data.token);
      setUser(data.user);
      initPushNotifications(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("kapei_token", data.token);
      setUser(data.user);
      initPushNotifications(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("kapei_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, error, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
